import { activeAiIssueTriageRunStatuses, project314McpEndpoint, scorecardGenerationToolOptions } from './constants';
import type {
    AiCandidateCurationRun,
    AiIssueTriageRun,
    AuditMetricScope,
    McpClientReadiness,
    ParameterDiscoveryMetric,
    ScorecardGenerationExternalTool,
    SourceAuditSummaryRow
} from './types';

// Approval badge styling is shared with the central AI Workbench approvals inbox.
export { getAIWorkApprovalBadgeVariant } from '@/components/AIWorkbench/helpers';

export const getScorecardGenerationToolLabel = (tool: ScorecardGenerationExternalTool): string =>
    scorecardGenerationToolOptions.find((option) => option.value === tool)?.label ?? 'External AI';

export const getMcpReadinessBadgeVariant = (status: string | undefined): 'success' | 'danger' | 'warning' | 'secondary' => {
    if (status === 'connected') return 'success';
    if (status === 'connector_required' || status === 'no_token') return 'warning';
    if (status === 'token_waiting') return 'warning';
    return 'secondary';
};

export const buildMcpSetupCommand = (tool: ScorecardGenerationExternalTool): string | null => {
    if (tool === 'codex') {
        return `export PROJECT314_MCP_TOKEN='paste-token-here'\ncodex mcp add project314 --url ${project314McpEndpoint} --bearer-token-env-var PROJECT314_MCP_TOKEN`;
    }

    if (tool === 'claude_code') {
        return `export PROJECT314_MCP_TOKEN='paste-token-here'\nclaude mcp add --transport http --scope user project314 ${project314McpEndpoint} --header "Authorization: Bearer \${PROJECT314_MCP_TOKEN}"`;
    }

    if (tool === 'cursor') {
        return `Project314 MCP endpoint: ${project314McpEndpoint}\nCreate a Project314 MCP token in Developer Access, then add this endpoint to Cursor's MCP configuration.`;
    }

    if (tool === 'chatgpt' || tool === 'claude') {
        return `Project314 MCP endpoint: ${project314McpEndpoint}\n${getScorecardGenerationToolLabel(tool)} requires a remote MCP app/connector before it can call Project314 tools.`;
    }

    return null;
};

export const getWorkbenchPanelReadiness = (
    session: { externalTool: ScorecardGenerationExternalTool } | null | undefined,
    sessionReadiness: McpClientReadiness | undefined,
    selectedTool: ScorecardGenerationExternalTool,
    fallbackReadiness: McpClientReadiness | undefined
): McpClientReadiness | undefined => {
    if (session?.externalTool === selectedTool) return sessionReadiness;
    return fallbackReadiness;
};

export const getDefaultAuditScope = (metric: ParameterDiscoveryMetric, hasOptionMetricData: boolean | undefined): AuditMetricScope =>
    metric === 'options' || hasOptionMetricData ? 'both' : 'stock';

export const getMetricsForAuditScope = (scope: AuditMetricScope): ParameterDiscoveryMetric[] => {
    if (scope === 'both') return ['stock', 'options'];
    return [scope];
};

export const getAuditScopeLabel = (scope: AuditMetricScope): string => {
    if (scope === 'both') return 'Stock + Options audit';
    return `${scope === 'options' ? 'Options' : 'Stock'} audit`;
};

export const formatCount = (value: number | null | undefined): string => (value ?? 0).toLocaleString();

export const formatLift = (value: number | null | undefined): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

export const formatTimestamp = (value: string | null | undefined): string => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

export const isAiIssueTriageRunActive = (run: AiIssueTriageRun | null | undefined): boolean =>
    !!run && activeAiIssueTriageRunStatuses.has(run.status);

export const isAiCandidateCurationRunActive = (run: AiCandidateCurationRun | null | undefined): boolean =>
    !!run && activeAiIssueTriageRunStatuses.has(run.status);

export const getAiBackendTriageButtonLabel = (running: boolean, pending: boolean): string => {
    if (running) return 'Triage Running...';
    if (pending) return 'Starting...';
    return 'Start Full AI Triage';
};

export const getAiCandidateCurationButtonLabel = (running: boolean, pending: boolean): string => {
    if (running) return 'Curation Running...';
    if (pending) return 'Starting...';
    return 'Start Full AI Curation';
};

export const getAiIssueTriageRunBadgeVariant = (status: string): 'success' | 'danger' | 'warning' | 'secondary' => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'running') return 'warning';
    return 'secondary';
};

export const getScorecardGenerationStatusBadgeVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'secondary' => {
    if (status === 'scorecard_created' || status === 'ready_for_review' || status === 'completed') return 'success';
    if (status === 'failed' || status === 'cancelled') return 'danger';
    if (status === 'in_progress') return 'info';
    if (status === 'waiting_for_agent' || status === 'approval_requested') return 'warning';

    // pending and any other queued/idle state read as a calm neutral rather than an attention-grabbing color.
    return 'secondary';
};

const scorecardGenerationTerminalStatuses: ReadonlySet<string> = new Set(['scorecard_created', 'failed', 'cancelled']);

export const isScorecardGenerationSessionTerminal = (status: string): boolean => scorecardGenerationTerminalStatuses.has(status);

export const getSourceAuditBadgeLabel = (row: SourceAuditSummaryRow): string => {
    if (!row.auditFresh) return 'Audit Pending';
    if (row.activeBackendIssueRefs > 0) return 'Needs Review';
    if (row.externalNeedsReviewRows > 0) return 'External Review';
    return 'Clean';
};
